/**
 * Styles for CrisisPractitionerPrompt and its sub-components.
 *
 * Extracted from CrisisPractitionerPrompt.tsx as part of E2 refactoring, S2.1 (#742).
 * All sub-components import styles from this shared module.
 *
 * TFunction is defined and exported here so sub-components don't need to re-import
 * from @dramadetector/shared directly.
 */

import { StyleSheet, Platform } from 'react-native';

/**
 * Type alias for the translation t function.
 * Uses a simple string-returning signature to avoid i18next branded type conflicts.
 * Defined here so all sub-components import from a single source.
 */
export type TFunction = (key: string, options?: Record<string, unknown>) => string;

export const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '85%',
    minHeight: 300,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },

  // Content Container
  contentContainer: {
    flex: 1,
  },

  // Header
  header: {
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 15,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 22,
  },

  // Description Section
  descriptionSection: {
    marginBottom: 24,
  },
  description: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 24,
    textAlign: 'center',
    marginBottom: 16,
  },

  // Privacy Notice
  privacyNotice: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#F3F4F6',
    padding: 16,
    borderRadius: 12,
    gap: 12,
  },
  privacyNoticeIcon: {
    fontSize: 16,
  },
  privacyNoticeText: {
    flex: 1,
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 18,
  },

  // Reminder Notice
  reminderNotice: {
    backgroundColor: '#FEF3C7',
    padding: 16,
    borderRadius: 12,
  },
  reminderNoticeText: {
    fontSize: 13,
    color: '#92400E',
    lineHeight: 18,
    textAlign: 'center',
  },

  // Error Detail
  errorDetail: {
    fontSize: 13,
    color: '#DC2626',
    textAlign: 'center',
    marginTop: 8,
  },

  // Loading
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },

  // No Practitioners
  noPractitionersContainer: {
    backgroundColor: '#F9FAFB',
    padding: 24,
    borderRadius: 12,
    marginBottom: 24,
    alignItems: 'center',
  },
  noPractitionersTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  noPractitionersDescription: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 20,
  },

  // Practitioner List
  practitionerList: {
    marginBottom: 24,
    gap: 12,
  },
  practitionerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  practitionerItemSelected: {
    borderColor: '#6366F1',
    backgroundColor: '#F5F3FF',
  },
  practitionerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  practitionerRadio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  practitionerRadioSelected: {
    borderColor: '#6366F1',
  },
  practitionerRadioDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#6366F1',
  },
  practitionerDetails: {
    flex: 1,
  },
  practitionerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  practitionerPreference: {
    fontSize: 13,
    color: '#6B7280',
  },

  // Center Content (for loading/sending states)
  centerContent: {
    paddingVertical: 60,
    alignItems: 'center',
    gap: 16,
  },
  sendingText: {
    fontSize: 16,
    color: '#6B7280',
  },

  // Button Container
  buttonContainer: {
    gap: 12,
  },

  // Primary Button
  primaryButton: {
    backgroundColor: '#6366F1', // indigo-500
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    minHeight: 56, // Crisis CTA touch target
    ...Platform.select({
      ios: {
        shadowColor: '#6366F1',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
      },
    }),
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  // Secondary Button
  secondaryButton: {
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
    minHeight: 52,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#6B7280',
  },

  // Disabled Button
  buttonDisabled: {
    backgroundColor: '#D1D5DB',
    ...Platform.select({
      ios: {
        shadowOpacity: 0,
      },
      android: {
        elevation: 0,
      },
      web: {
        boxShadow: 'none',
      },
    }),
  },
});
